// Static UI content for LytePress dashboard (deals/businesses come from the API)
import {
  Utensils, Coffee, HeartPulse, Ticket, BedDouble, Wrench, PawPrint, Sparkles,
  Smartphone, CalendarClock, Car, Dumbbell, Shirt, Leaf, Package,
  Wallet, Recycle, HeartHandshake, Plane, GraduationCap, Tractor
} from "lucide-react";

export const heroSlides = [
  {
    id: 1,
    titleKeys: ["home.slide1a", "home.slide1b"],
    subtitleKey: "home.slide1sub",
    badgeKey: "home.slide1badge",
    image: "https://images.unsplash.com/photo-1588964895597-cfccd6e2dbf9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxncm9jZXJ5JTIwYmFnfGVufDB8fHx8MTc4NjMzMjMyN3ww&ixlib=rb-4.1.0&q=85",
  },
  {
    id: 2,
    titleKeys: ["home.slide2a", "home.slide2b"],
    subtitleKey: "home.slide2sub",
    badgeKey: "home.slide2badge",
    image: "https://images.unsplash.com/photo-1610348725531-843dff563e2c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHw0fHxmcmVzaCUyMHZlZ2V0YWJsZXN8ZW58MHx8fHwxNzg2MzMyMzI3fDA&ixlib=rb-4.1.0&q=85",
  },
  {
    id: 3,
    titleKeys: ["home.slide3a", "home.slide3b"],
    subtitleKey: "home.slide3sub",
    badgeKey: "home.slide3badge",
    image: "https://images.unsplash.com/photo-1509440159596-0249088772ff?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwzfHxiYWtlcnklMjBicmVhZHxlbnwwfHx8fDE3ODYzMzIzMjd8MA&ixlib=rb-4.1.0&q=85",
  },
];

export const categories = [
  { name: "Food", slug: "food", icon: Utensils, color: "#16a34a", bg: "#e7f6ec" },
  { name: "Drinks", slug: "drinks", icon: Coffee, color: "#d97706", bg: "#fdf2e0" },
  { name: "Health", slug: "health", icon: HeartPulse, color: "#e11d48", bg: "#fde8ee" },
  { name: "Events", slug: "events", icon: Ticket, color: "#9333ea", bg: "#f3e8fd" },
  { name: "Lodging", slug: "lodging", icon: BedDouble, color: "#2563eb", bg: "#e6effd" },
  { name: "Services", slug: "services", icon: Wrench, color: "#475569", bg: "#eef1f5" },
  { name: "Pets", slug: "pets", icon: PawPrint, color: "#ea580c", bg: "#fdece0" },
  { name: "Beauty", slug: "beauty", icon: Sparkles, color: "#db2777", bg: "#fce7f3" },
  { name: "Tech", slug: "tech", icon: Smartphone, color: "#4f46e5", bg: "#e8e7fd" },
  { name: "Near Expiry", slug: "near-expiry", icon: CalendarClock, color: "#dc2626", bg: "#fde8e8" },
  { name: "Upcoming", slug: "upcoming", icon: CalendarClock, color: "#2563eb", bg: "#e6effd" },
  { name: "Automotive", slug: "automotive", icon: Car, color: "#0891b2", bg: "#e2f4f7" },
  { name: "Fitness", slug: "fitness", icon: Dumbbell, color: "#0d9488", bg: "#d9f2ef" },
  { name: "Clothing", slug: "clothing", icon: Shirt, color: "#c026d3", bg: "#fbe8fd" },
  { name: "Plants", slug: "plants", icon: Leaf, color: "#059669", bg: "#dcf5ec" },
  { name: "Education", slug: "education", icon: GraduationCap, color: "#4338ca", bg: "#e6e7fd" },
  { name: "Farm & Livestock", slug: "farm-livestock", icon: Tractor, color: "#65a30d", bg: "#edf7dc" },
  { name: "Travel & Tours", slug: "travel-tours", icon: Plane, color: "#0369a1", bg: "#e0f2fe" },
  { name: "Other", slug: "other", icon: Package, color: "#64748b", bg: "#eef1f5" },
];

export const valueProps = [
  { icon: Wallet, titleKey: "home.vp1t", textKey: "home.vp1x", color: "#16a34a", bg: "#e7f6ec" },
  { icon: Recycle, titleKey: "home.vp2t", textKey: "home.vp2x", color: "#0891b2", bg: "#e2f4f7" },
  { icon: HeartHandshake, titleKey: "home.vp3t", textKey: "home.vp3x", color: "#9333ea", bg: "#f3e8fd" },
];

// Caribbean countries and their cities (kept in sync with backend COUNTRY_CITY_COORDS)
export const countries = [
  { name: "Trinidad & Tobago", cities: ["Port of Spain", "Chaguanas", "San Fernando", "Arima", "Sangre Grande", "Point Fortin", "Couva", "Scarborough", "Tunapuna", "Diego Martin", "Princes Town", "Rio Claro"] },
  { name: "Jamaica", cities: ["Kingston", "Montego Bay", "Spanish Town", "Portmore", "Mandeville", "May Pen", "Ocho Rios", "Negril", "Port Antonio", "Savanna-la-Mar"] },
  { name: "Barbados", cities: ["Bridgetown", "Speightstown", "Oistins", "Holetown", "Bathsheba", "Six Cross Roads"] },
  { name: "Guyana", cities: ["Georgetown", "Linden", "New Amsterdam", "Anna Regina", "Bartica", "Lethem"] },
  { name: "Grenada", cities: ["St. George's", "Gouyave", "Grenville", "Sauteurs", "Victoria"] },
  { name: "Saint Lucia", cities: ["Castries", "Gros Islet", "Vieux Fort", "Soufriere", "Micoud", "Dennery"] },
];

export const citiesFor = (country) => (countries.find((c) => c.name === country)?.cities) || [];

// Map any deal category (including legacy names) to its icon + colors for the card chip
const CATEGORY_ALIASES = {
  Restaurants: "Food", Bakery: "Food", Groceries: "Food", "Food & Beverages": "Food",
  Electronics: "Tech", "Health & Beauty": "Health",
};
export const categoryMeta = (name) => {
  const canonical = CATEGORY_ALIASES[name] || name;
  return categories.find((c) => c.name === canonical) || categories.find((c) => c.name === "Other");
};

// Curated, brand-quality hero images per category (fallback = Food). Used by the data-driven hero
// so a real top deal is shown with a relevant, high-quality image instead of a variable deal photo.
export const CATEGORY_HERO_IMAGES = {
  Food: "https://static.prod-images.emergentagent.com/jobs/14b20dd2-ded5-4e1d-ab8a-fb9065fae046/images/9080d171066f429b8b871417b7f0f16c8746214ddf9420306dfb20cc21aabfda.jpeg",
  Drinks: "https://static.prod-images.emergentagent.com/jobs/14b20dd2-ded5-4e1d-ab8a-fb9065fae046/images/d6398ce759b615d7e794822c5dc1b8876e39718fcfe176bc4a6a345a5086a8c6.jpeg",
  Health: "https://static.prod-images.emergentagent.com/jobs/14b20dd2-ded5-4e1d-ab8a-fb9065fae046/images/91a4b05e4c9bfb1416873f6522abfd016bca213f045dc956cd5829687b82c13b.jpeg",
  Beauty: "https://static.prod-images.emergentagent.com/jobs/14b20dd2-ded5-4e1d-ab8a-fb9065fae046/images/31185bac9bdddfb89981b95fb07b7a204589b7c435d26df3dac2dc23fe6664e8.jpeg",
  Tech: "https://static.prod-images.emergentagent.com/jobs/14b20dd2-ded5-4e1d-ab8a-fb9065fae046/images/929de9f1c153c2727cb9081cf297facadf87ca7bf85e3c0bb954bf14a27eea38.jpeg",
  Clothing: "https://static.prod-images.emergentagent.com/jobs/14b20dd2-ded5-4e1d-ab8a-fb9065fae046/images/83934ce9ee23f299d7e26f9134c80ec47640f0d3838bc5d409357f0d3078e8bb.jpeg",
  Fitness: "https://static.prod-images.emergentagent.com/jobs/14b20dd2-ded5-4e1d-ab8a-fb9065fae046/images/9f3aa0236b0889879c49a51db413bf37625f7dcd8a5568644c263503720fe5fe.jpeg",
  Services: "https://static.prod-images.emergentagent.com/jobs/14b20dd2-ded5-4e1d-ab8a-fb9065fae046/images/292546fb13faf498ab59b76adf29d22803457251e329dd02b71d60a419a8c390.jpeg",
  Events: "https://static.prod-images.emergentagent.com/jobs/14b20dd2-ded5-4e1d-ab8a-fb9065fae046/images/7aeeb9ffebb6e903a015fcc9f72d5056eba3c3f5e4b8a01bb30c049f30fa253c.jpeg",
  Lodging: "https://static.prod-images.emergentagent.com/jobs/14b20dd2-ded5-4e1d-ab8a-fb9065fae046/images/b1bb29fcb0a3e5deb58e6985ac5dc9d48d2dacebcdf1ccef666402afb99676fc.jpeg",
  Education: "https://static.prod-images.emergentagent.com/jobs/14b20dd2-ded5-4e1d-ab8a-fb9065fae046/images/7bdd60ced0ca99b7893099d41ff992a84ab1cad5c2addd9a18f9d69900b436cc.jpeg",
};
export const categoryHeroImage = (name) =>
  CATEGORY_HERO_IMAGES[(categoryMeta(name) || {}).name] || CATEGORY_HERO_IMAGES.Food;

// Optional category-specific hero headline, so the curated image gets matching on-brand copy.
// Keyed by canonical category name; falls back to the rotating generic slides when absent.
export const CATEGORY_HERO_COPY = {
  Education: { titleKeys: ["home.eduHeroA", "home.eduHeroB"], subtitleKey: "home.eduHeroSub" },
};
export const categoryHeroCopy = (name) => CATEGORY_HERO_COPY[(categoryMeta(name) || {}).name] || null;
